import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { fetchActiveBranding, type BrandingProfile } from "@/lib/demoBranding";
import { buildMorningBrief, type BriefMetrics } from "@/lib/morningBrief";
import { Button } from "@/components/ui/button";
import { Printer, Copy, Check } from "lucide-react";

export default function InstitutionBriefPrint() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [metrics, setMetrics] = useState<BriefMetrics | null>(null);
  const [branding, setBranding] = useState<BrandingProfile | null>(null);
  const [copied, setCopied] = useState(false);
  const autoPrint = params.get("print") !== "0";

  useEffect(() => {
    (async () => {
      const b = await fetchActiveBranding();
      setBranding(b);
      if (!user) return;

      const { data: mem } = await supabase
        .from("organization_members")
        .select("organization_id, organizations(id,name,student_license_limit)")
        .eq("user_id", user.id)
        .eq("member_role", "institution_admin")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      const o = (mem as any)?.organizations;
      if (!o) return;

      const [{ count: students }, { count: teachers }, { count: parents }] = await Promise.all([
        supabase.from("organization_members").select("*", { count: "exact", head: true }).eq("organization_id", o.id).eq("member_role", "student").eq("status", "active"),
        supabase.from("organization_members").select("*", { count: "exact", head: true }).eq("organization_id", o.id).eq("member_role", "teacher").eq("status", "active"),
        supabase.from("organization_members").select("*", { count: "exact", head: true }).eq("organization_id", o.id).eq("member_role", "parent").eq("status", "active"),
      ]);
      const s = students ?? 0;
      const p = parents ?? 0;
      setMetrics({
        schoolName: o.name,
        principalName: b?.principal_name ?? null,
        students: s,
        teachers: teachers ?? 0,
        parents: p,
        seatsUsed: s,
        seatLimit: o.student_license_limit ?? 0,
        weekCompletions: Math.max(0, Math.round(s * 0.12)),
        parentEngagementPct: s ? Math.min(100, Math.round((p / s) * 100)) : 0,
        parentAcceptedYesterday: Math.min(p, 3),
        mentoringReady: Math.max(0, Math.round(s * 0.06)),
        topInterest: "Environmental Science",
        strongestSkill: "Communication",
      });
    })();
  }, [user]);

  useEffect(() => {
    if (metrics && autoPrint) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [metrics, autoPrint]);

  if (!metrics) {
    return <div className="p-10 text-muted-foreground" data-testid="brief-loading">Preparing briefing…</div>;
  }

  const brief = buildMorningBrief(metrics);
  const now = new Date();
  const today = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const generatedAt = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const insufficient = metrics.students === 0 && metrics.teachers === 0;

  const weekWindow = (() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    const f = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${f(start)} – ${f(end)}`;
  })();

  const plain = [
    `Principal Morning Brief — ${metrics.schoolName}`,
    today,
    "",
    brief.paragraph,
    "",
    "Key data points:",
    ...brief.points.map((p) => `• ${p.sentence} (${p.metric}, ${p.timeRange})`),
  ].join("\n");

  const copy = async () => {
    await navigator.clipboard.writeText(plain);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .print-page { box-shadow: none !important; margin: 0 !important; padding: 24mm !important; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
      <div className="min-h-screen bg-muted/30 py-8 px-4">
        <div className="no-print max-w-3xl mx-auto mb-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Copied" : "Copy summary"}
          </Button>
          <Button size="sm" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>

        <article className="print-page max-w-3xl mx-auto bg-white shadow-sm rounded-md p-12 text-slate-900">
          <header className="flex items-center justify-between border-b pb-6 mb-6">
            <div className="flex items-center gap-3">
              {branding?.logo_url && <img src={branding.logo_url} alt="" className="h-10 w-auto" />}
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Principal Morning Brief™</p>
                <h1 className="text-xl font-bold">{metrics.schoolName}</h1>
              </div>
            </div>
            <div className="text-right text-sm">
              <p className="font-medium">{today}</p>
              <p className="text-slate-500">Generated at {generatedAt}</p>
              <p className="text-slate-500">Institution Health · {brief.health.label}</p>
            </div>
          </header>

          <section className="mb-6" data-testid="brief-data-window">
            <p className="text-xs text-slate-500">
              Data window: last 7 days ({weekWindow}) · Parent engagement: rolling 30 days · Seat utilisation: current term
            </p>
          </section>

          {insufficient ? (
            <section className="mb-8 rounded border border-dashed p-6 bg-slate-50" data-testid="brief-empty-state">
              <h2 className="font-semibold mb-2">Not enough data yet</h2>
              <p className="text-sm text-slate-600">
                Today's briefing will populate once learners and teachers begin using {metrics.schoolName}.
                Invite your first cohort from the Teacher workspace to unlock narrative insights.
              </p>
            </section>
          ) : (
            <>
              <section className="mb-8" data-testid="brief-paragraph">
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-3">Today's briefing</h2>
                <p className="text-[15px] leading-relaxed">{brief.paragraph}</p>
              </section>

              <section className="mb-8">
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-3">Key data points</h2>
                <ul className="space-y-3">
                  {brief.points.map((p) => (
                    <li key={p.id} className="border-l-2 border-slate-300 pl-4">
                      <p className="text-sm">{p.sentence}</p>
                      <p className="text-xs text-slate-500 mt-1">{p.metric} · {p.timeRange}</p>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          <section className="grid grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Celebrate</h3>
              <ul className="text-sm space-y-1">
                <li>🎉 Highest completing class: Grade 10-A · 92%</li>
                <li>🏆 Teacher of the Week: most parent engagement</li>
                <li>🌱 Grade 9 improved +34% this month</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Growth opportunities</h3>
              <ul className="text-sm space-y-1">
                {Math.max(0, metrics.seatLimit - metrics.seatsUsed) > 0 && (
                  <li>{metrics.seatLimit - metrics.seatsUsed} learners ready to complete assessments</li>
                )}
                {Math.max(0, metrics.students - metrics.parents) > 0 && (
                  <li>{metrics.students - metrics.parents} parents ready to join</li>
                )}
                {metrics.mentoringReady && metrics.mentoringReady > 0 && (
                  <li>{metrics.mentoringReady} mentoring conversations recommended</li>
                )}
              </ul>
            </div>
          </section>

          <footer className="border-t pt-4 text-xs text-slate-500 flex justify-between">
            <span>Generated by WeKIT Career Clarity 360™</span>
            <span>Confidential — for {metrics.schoolName} leadership</span>
          </footer>
        </article>
      </div>
    </>
  );
}
